import { Capacitor } from "@capacitor/core";
import { SecurePreferences } from "@capawesome-team/capacitor-secure-preferences";
import { SecureStoragePlugin } from "capacitor-secure-storage-plugin";
import { env } from "~/config/env";
import { captureException, captureMessage } from "~/config/sentry";

console.info("🔑 window.location", window.location);
console.info("🔑 env", env);

class SecureCache {
  async get<T = any>(key: string): Promise<T | null> {
    try {
      if (Capacitor.isPluginAvailable("SecurePreferences")) {
        const cacheResult = await SecurePreferences.get({ key });
        console.info("🔑 Using Capawesome SecurePreferences cache", {
          cacheResult,
          key,
        });
        if (typeof cacheResult === "string") {
          return JSON.parse(cacheResult) as T;
        } else {
          captureMessage("🔐 No storage element returned");
          return null;
        }
      } else if (Capacitor.isPluginAvailable("SecureStoragePlugin")) {
        const oldCacheResult = await SecureStoragePlugin.get({ key });
        console.info("🔑 Using Capacitor SecureStoragePlugin cache", {
          key,
          oldCacheResult,
        });
        return JSON.parse(oldCacheResult.value) as T;
      } else {
        captureMessage("🔐🔴 No secure storage plugin available");
        return null;
      }
    } catch (e) {
      console.warn(e, { extra: { function: "CapacitorCacheGetError", key } });
      return null;
    }
  }

  async set<T = any>(key: string, entry: T): Promise<void> {
    try {
      if (Capacitor.isPluginAvailable("SecurePreferences")) {
        console.info("🔑 Using Capawesome SecurePreferences cache", {
          entry,
          key,
        });
        await SecurePreferences.set({ key, value: JSON.stringify(entry) });
      } else if (Capacitor.isPluginAvailable("SecureStoragePlugin")) {
        console.info("🔑 Using Capacitor SecureStoragePlugin cache", {
          entry,
          key,
        });
        await SecureStoragePlugin.set({ key, value: JSON.stringify(entry) });
      } else {
        captureMessage("🔐🔴 No secure storage plugin available");
      }
    } catch (e) {
      captureException(e, {
        extra: { entry, function: "CapacitorCacheSetError", key },
      });
    }
  }

  async remove(key: string): Promise<void> {
    try {
      if (Capacitor.isPluginAvailable("SecurePreferences")) {
        console.info("🔑 Using Capawesome SecurePreferences cache", {
          key,
        });
        await SecurePreferences.remove({ key });
      } else if (Capacitor.isPluginAvailable("SecureStoragePlugin")) {
        console.info("🔑 Using Capacitor SecureStoragePlugin cache", {
          key,
        });
        await SecureStoragePlugin.remove({ key });
      } else {
        captureMessage("🔐🔴 No secure storage plugin available");
      }
    } catch (e) {
      captureException(e, {
        extra: {
          function: "CapacitorCacheRemoveError",
          key,
        },
      });
    }
  }

  async allKeys(): Promise<string[]> {
    try {
      if (Capacitor.isPluginAvailable("SecurePreferences")) {
        const allKeys = await SecurePreferences.keys();
        console.info("🔑 Using Capawesome SecurePreferences cache", {
          allKeys,
        });
        return allKeys.keys;
      } else if (Capacitor.isPluginAvailable("SecureStoragePlugin")) {
        const allKeys = await SecureStoragePlugin.keys();
        console.info("🔑 Using Capacitor SecureStoragePlugin cache", {
          allKeys,
        });
        return allKeys.value;
      } else {
        captureMessage("🔐🔴 No secure storage plugin available");
        return [];
      }
    } catch (e) {
      captureException(e, {
        extra: {
          function: "CapacitorCacheallKeysError",
        },
      });
      return [];
    }
  }
}

export { SecureCache };
